import { useIntl } from "react-intl";
import AppsContainer from "@lib/components/AppComponents/AppsContainer";
import { useEffect, useMemo, useState } from "react";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import getColumns from "./columns";
import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import { TableHeaderFilter } from "@lib/types/Api";
import { TransactionType } from "@lib/types/Transaction";
import { handleTableChange } from "@lib/helpers/UrlHelper";
import { type TableProps } from "antd";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import { useLocation, useNavigate } from "react-router-dom";
import AppTable from "@lib/components/MiniComponents/Table/AppTable";
// import { useBlockchainTransactionQueries } from "@lib/apiQueries";
import {
  AuthRoles,
  CustomerTransactionPayload,
  Notification,
  SharedContextProps,
} from "@lib/types";
import { PaymentHistoryDrawer } from "@lib/components/PageComponents/PaymentHistory";
import { useQuery } from "@tanstack/react-query";
import { apiGetTransactions } from "@lib/api/transaction";
import { useTypedSelector } from "@lib/redux";
import useScreen from "@lib/hooks/useScreen";

const SharedPaymentHistory: React.FC<SharedContextProps> = ({
  context,
  detailContext,
  providerId,
  customTitle,
  withoutContainer = false,
  pageIsActive = true,
  minusHeight = 0,
  tableProps,
  height,
}) => {
  const { messages } = useIntl();
  const navigate = useNavigate();
  const [openModal, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<TransactionType>();
  const { user } = useTypedSelector((state) => state.auth);
  const userRole = user?.role;
  // notificationState
  const { state } = useLocation();
  const notificationState = state?.notificationState as Notification;
  const highlightedRecord = useMemo(() => {
    const payload = notificationState?.payload as CustomerTransactionPayload;
    if (payload?.transaction) {
      return {
        id: payload?.transaction?.id,
      };
    }
    return null;
  }, [notificationState]);
  const { isMobile } = useScreen();

  useEffect(() => {
    if (!openModal) setSelected(null);
  }, [openModal]);

  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };

  const handleSelect = (item: TransactionType) => {
    setSelected(item);
    toggle(true);
  };

  const initialFilters = [
    ...(providerId ? [`providerId||$eq||${providerId}`] : []),
  ];

  const { params, setParams, handleRemoveFilters } = useParamsManager({
    page: 1,
    limit: 100,
    sort: "externalId,DESC",
    join: ["customer", "provider"],
    filter: initialFilters,
  });

  const blockchainTransactionsQuery = useQuery({
    queryFn: () => apiGetTransactions(params),
    queryKey: ["payment-history-transaction", JSON.stringify(params)],
    enabled: !!params && !!pageIsActive,
    refetchInterval: 1000 * 60,
  });
  const { data, isLoading, isFetching } = blockchainTransactionsQuery;
  const apiData = data?.data?.data;

  const onPageChange = (page: number, limit: number) => {
    if (page !== params.page || limit !== params.limit) {
      setParams({ ...params, page, limit });
    }
  };

  const onChange: TableProps<TransactionType>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    handleTableChange({ pagination, filters, sorter, params, setParams });
  };

  const columns = useMemo(
    () =>
      getColumns({
        isMobile,
        context,
        apiData,
        params,
        navigate,
        messages,
        handleSelect,
        userRole,
        columnsConfig: tableProps?.columnsConfig,
      }),
    [apiData, params, tableProps?.columnsConfig],
  );

  const hiddenFilters = useMemo(() => {
    if (!!detailContext) {
      return ["providerId"];
    }
    return [];
  }, [detailContext]);

  const filters = useMemo(() => {
    if (!params?.filter) return [];

    return params?.filter
      .map((f) => ({
        key: f.split("||")[0],
        title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
        operator: f.split("||")[1],
        value: f.split("||")[2],
      }))
      .filter((f) => !hiddenFilters.includes(f.key)) as TableHeaderFilter[];
  }, [params, columns, hiddenFilters]);

  const title = customTitle || (messages["common.paymentHistory"] as string);

  const canAddMore = useMemo(() => {
    return (
      userRole === AuthRoles.Admin ||
      userRole === AuthRoles.CustomerAgent ||
      userRole === AuthRoles.CustomerManager
    );
  }, [userRole, context]);

  return (
    <AppsContainer
      fullView={true}
      title={title}
      noContainer={withoutContainer}
      minusHeight={minusHeight}
      cardProps={{
        borderFull: true,
        coloredHeader: true,
      }}
    >
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 10}
          onPageChange={onPageChange}
          query={blockchainTransactionsQuery}
          filters={filters}
          handleRemoveFilters={handleRemoveFilters}
          plusProps={
            canAddMore
              ? {
                  onClick: () => toggle(true),
                  disabled: isLoading,
                  loading: isLoading,
                }
              : undefined
          }
          tableProps={{ onChange, columns, ...tableProps }}
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          dataSource={apiData}
          columns={columns}
          rowKey={"id"}
          loading={isLoading || isFetching}
          onChange={onChange}
          scroll={{ y: height }}
          minusHeight={minusHeight}
          highlightedRecord={highlightedRecord}
          {...tableProps}
        />
      </AppsContent>

      <PaymentHistoryDrawer
        open={openModal}
        toggle={toggle}
        selected={selected}
        handleSelect={handleSelect}
        query={blockchainTransactionsQuery}
      />
    </AppsContainer>
  );
};

export default SharedPaymentHistory;
